import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error('Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.');
}

// Active tenant ID — set by TenantContext after domain detection.
// Injected as x-tenant-id header on every Supabase REST request so that
// PostgreSQL RLS functions (get_current_tenant_id) can scope data to
// the correct tenant for multi-tenant users.
let _activeTenantId: string | null = null;

const tenantAwareFetch: typeof fetch = (input, init) => {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));

  if (_activeTenantId) {
    headers.set('x-tenant-id', _activeTenantId);
  } else {
    headers.delete('x-tenant-id');
  }

  return fetch(input, { ...init, headers });
};

// Keep one auth client for the lifetime of the page. Creating another client
// against the same localStorage key starts a second refresh loop and can leave
// Edge Function calls carrying a stale token. The fetch wrapper reads the
// current tenant at request time, so tenant-aware RLS does not require a new
// Supabase client.
export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    fetch: tenantAwareFetch,
  },
});

/**
 * Set the active tenant for all subsequent Supabase requests.
 * The singleton client's fetch wrapper adds the x-tenant-id header so
 * PostgREST exposes it to RLS functions via current_setting('request.headers').
 */
export function setActiveTenantId(tenantId: string | null): void {
  if (tenantId === _activeTenantId) return; // No-op if unchanged
  _activeTenantId = tenantId;
}

/** Get the currently active tenant ID (for debugging/logging). */
export function getActiveTenantId(): string | null {
  return _activeTenantId;
}
